import { getRuntimeConfig } from '../config/runtimeConfig.js';
import { getIdToken, renewIdToken } from '../auth/oidc.js';
import { makeCallable, type CallableFn } from './callableClient.js';
import type {
  CreateJobRequest,
  SmartFillRequest,
  SmartFillResponse,
  CreateJobResponse,
  TriggerEc2S3UploadRequest,
  TriggerEc2S3UploadResponse,
  ListEc2FoldersResponse,
  GetEc2HealthResponse,
  FlushEc2InstanceResponse,
  GetEc2FlushStatusResponse,
  CancelJobRequest,
  CancelJobResponse,
  RetryJobRequest,
  RetryJobResponse,
  StartMountRequest,
  StartMountResponse,
  DropMountDatabaseRequest,
  DropMountDatabaseResponse,
  GetDestinationConfigResponse,
  VerifyDestinationBucketRequest,
  VerifyDestinationBucketResponse,
  SetDestinationBucketRequest,
  SetDestinationBucketResponse,
  GetCrmConfigResponse,
  GetCrmAssignmentsResponse,
  SetCrmConfigRequest,
  SetCrmConfigResponse,
  CreateExportRequest,
  CreateExportResponse,
  CreateRedshiftExportRequest,
  CreateRedshiftExportResponse,
  CancelRedshiftExportRequest,
  CancelRedshiftExportResponse,
  DeleteRedshiftExportRequest,
  DeleteRedshiftExportResponse,
  CreateMoveAttachmentsRequest,
  CreateMoveAttachmentsResponse,
  ConfirmMoveAttachmentsRequest,
  ConfirmMoveAttachmentsResponse,
  CancelMoveAttachmentsRequest,
  CancelMoveAttachmentsResponse,
  RerunMoveAttachmentsRequest,
  RerunMoveAttachmentsResponse,
  ChangeMoveAttachmentsScopeRequest,
  ChangeMoveAttachmentsScopeResponse,
  DeleteMoveAttachmentsRequest,
  DeleteMoveAttachmentsResponse,
  PurgeMoveAttachmentsDestinationRequest,
  PurgeMoveAttachmentsDestinationResponse,
  GetExportCredentialsRequest,
  GetExportCredentialsResponse,
  ListExportContentsRequest,
  ListExportContentsResponse,
  ListAgenciesResponse,
  ListAgencyUsersRequest,
  ListAgencyUsersResponse,
  GetExportSettingsResponse,
  SetExportTablesRequest,
  SetExportTablesResponse,
  ListJobsRequest,
  ListJobsResponse,
  ListExportsResponse,
  ListPageRequest,
  ListRedshiftExportsResponse,
  ListMoveAttachmentsResponse,
  ListMoveAttachmentLogsResponse,
} from '@atlas/shared';

export { CallableError } from './callableClient.js';

/** A typed callable on the router Lambda; call it as `fn(data)` → `{ data }`. */
function callable<Req, Res>(name: string, opts: { timeout?: number } = {}): CallableFn<Req, Res> {
  return makeCallable<Req, Res>(
    {
      apiUrl: () => getRuntimeConfig().apiUrl,
      getToken: getIdToken,
      renewToken: renewIdToken,
      fetch: (...args) => fetch(...args),
    },
    name,
    opts,
  );
}

export const createJob = callable<CreateJobRequest, CreateJobResponse>('createJob');

export const smartFill = callable<SmartFillRequest, SmartFillResponse>('smartFill');

export const triggerEc2S3Upload = callable<
  TriggerEc2S3UploadRequest,
  TriggerEc2S3UploadResponse
>('triggerEc2S3Upload');

export const listEc2Folders = callable<void, ListEc2FoldersResponse>('listEc2Folders');

export const getEc2Health = callable<void, GetEc2HealthResponse>('getEc2Health');

/** Starts the flush; getEc2FlushStatus reports it until complete or failed. */
export const flushEc2Instance = callable<void, FlushEc2InstanceResponse>('flushEc2Instance');
export const getEc2FlushStatus = callable<void, GetEc2FlushStatusResponse>('getEc2FlushStatus');

export interface AllowedUser {
  id: string;
  email: string;
  isAdmin: boolean;
  addedBy: string;
  displayName: string | null;
  uid: string | null;
}

export const deleteJob = callable<{ jobId: string }, { success: boolean }>('deleteJob');
export const cancelJob = callable<CancelJobRequest, CancelJobResponse>('cancelJob');
export const retryJob = callable<RetryJobRequest, RetryJobResponse>('retryJob');
export const startMount = callable<StartMountRequest, StartMountResponse>('startMount');
export const dropMountDatabase = callable<
  DropMountDatabaseRequest,
  DropMountDatabaseResponse
>('dropMountDatabase');
export const addUser = callable<{ email: string; isAdmin?: boolean }, { success: boolean }>('addUser');
export const removeUser = callable<{ email: string }, { success: boolean }>('removeUser');
export const setAdmin = callable<{ email: string; isAdmin: boolean }, { success: boolean }>('setAdmin');
export const upsertSelf = callable<void, { allowed: boolean; isAdmin: boolean }>('upsertSelf');
export const listUsers = callable<void, { users: AllowedUser[] }>('listUsers');

export const getDestinationConfig = callable<void, GetDestinationConfigResponse>('getDestinationConfig');
export const verifyDestinationBucket = callable<
  VerifyDestinationBucketRequest,
  VerifyDestinationBucketResponse
>('verifyDestinationBucket');
export const setDestinationBucket = callable<
  SetDestinationBucketRequest,
  SetDestinationBucketResponse
>('setDestinationBucket');

export const createExport = callable<CreateExportRequest, CreateExportResponse>('createExport');
export const listAgencies = callable<void, ListAgenciesResponse>('listAgencies');
export const listAgencyUsers = callable<
  ListAgencyUsersRequest,
  ListAgencyUsersResponse
>('listAgencyUsers');
export const getExportSettings = callable<void, GetExportSettingsResponse>('getExportSettings');
export const setExportTables = callable<
  SetExportTablesRequest,
  SetExportTablesResponse
>('setExportTables');
export const getExportCredentials = callable<
  GetExportCredentialsRequest,
  GetExportCredentialsResponse
>('getExportCredentials');
export const listExportContents = callable<
  ListExportContentsRequest,
  ListExportContentsResponse
>('listExportContents');

export const createRedshiftExport = callable<
  CreateRedshiftExportRequest,
  CreateRedshiftExportResponse
>('createRedshiftExport');
export const cancelRedshiftExport = callable<
  CancelRedshiftExportRequest,
  CancelRedshiftExportResponse
>('cancelRedshiftExport');
export const deleteRedshiftExport = callable<
  DeleteRedshiftExportRequest,
  DeleteRedshiftExportResponse
>('deleteRedshiftExport');

export const createMoveAttachments = callable<
  CreateMoveAttachmentsRequest,
  CreateMoveAttachmentsResponse
>('createMoveAttachments');
export const confirmMoveAttachments = callable<
  ConfirmMoveAttachmentsRequest,
  ConfirmMoveAttachmentsResponse
>('confirmMoveAttachments');
export const cancelMoveAttachments = callable<
  CancelMoveAttachmentsRequest,
  CancelMoveAttachmentsResponse
>('cancelMoveAttachments');
export const rerunMoveAttachments = callable<
  RerunMoveAttachmentsRequest,
  RerunMoveAttachmentsResponse
>('rerunMoveAttachments');
export const changeMoveAttachmentsScope = callable<
  ChangeMoveAttachmentsScopeRequest,
  ChangeMoveAttachmentsScopeResponse
>('changeMoveAttachmentsScope');
export const deleteMoveAttachments = callable<
  DeleteMoveAttachmentsRequest,
  DeleteMoveAttachmentsResponse
>('deleteMoveAttachments');
export const purgeMoveAttachmentsDestination = callable<
  PurgeMoveAttachmentsDestinationRequest,
  PurgeMoveAttachmentsDestinationResponse
>('purgeMoveAttachmentsDestination');

export const getCrmConfig = callable<void, GetCrmConfigResponse>('getCrmConfig');
export const getCrmAssignments = callable<void, GetCrmAssignmentsResponse>('getCrmAssignments');
export const setCrmConfig = callable<
  SetCrmConfigRequest,
  SetCrmConfigResponse
>('setCrmConfig');

// ─── List views (polled by lib/jobs.ts, exports.ts, redshiftExports.ts, moveAttachments.ts)
export const listJobs = callable<ListJobsRequest, ListJobsResponse>('listJobs');
export const listExports = callable<void, ListExportsResponse>('listExports');
export const listRedshiftExports = callable<ListPageRequest, ListRedshiftExportsResponse>(
  'listRedshiftExports',
);
export const listMoveAttachments = callable<ListPageRequest, ListMoveAttachmentsResponse>(
  'listMoveAttachments',
);
export const listMoveAttachmentLogs = callable<ListPageRequest, ListMoveAttachmentLogsResponse>(
  'listMoveAttachmentLogs',
);
